import { useMemo, useState } from "react";
import { X, Check, Trash, ArrowRight, Stack, Sparkle } from "@phosphor-icons/react";
import { photoUrl, roomLabel } from "./walkApi";
import { RoomIcon } from "./RoomListScreen";

const GAP_MS = 90 * 1000;

// Group photos shot within 90s of each other → one tap moves a whole cluster.
function cluster(photos) {
  const sorted = [...photos].sort((a, b) => (a.taken_at || a.uploaded_at || "").localeCompare(b.taken_at || b.uploaded_at || ""));
  const out = [];
  for (const p of sorted) {
    const t = Date.parse(p.taken_at || p.uploaded_at || 0);
    const last = out[out.length - 1];
    if (last && t - last.end <= GAP_MS) { last.photos.push(p); last.end = t; }
    else out.push({ start: t, end: t, photos: [p] });
  }
  return out;
}

export default function SortBoard({ rooms, photos, onAssign, onDelete, onClose, onImportMore, onAiSort }) {
  const [sel, setSel] = useState(new Set());
  const [target, setTarget] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const suggested = photos.filter(p => p.suggested_room_idx != null);
  const groups = useMemo(() => {
    if (suggested.length === 0) return cluster(photos);
    // Group by AI-suggested room; unsuggested go last
    const by = new Map();
    photos.forEach(p => { const k = p.suggested_room_idx ?? -1; if (!by.has(k)) by.set(k, []); by.get(k).push(p); });
    return [...by.entries()].sort((a, b) => (a[0] === -1) - (b[0] === -1) || a[0] - b[0])
      .map(([k, ps]) => ({ start: 0, end: 0, photos: ps, roomIdx: k === -1 ? null : k }));
  }, [photos, suggested.length]);
  const runAi = async () => { setAiBusy(true); try { await onAiSort(); } finally { setAiBusy(false); } };
  const applyAll = async () => {
    const byRoom = new Map();
    suggested.forEach(p => { if (!byRoom.has(p.suggested_room_idx)) byRoom.set(p.suggested_room_idx, []); byRoom.get(p.suggested_room_idx).push(p.path); });
    for (const [ridx, paths] of byRoom) await onAssign(paths, ridx);
    setSel(new Set());
  };
  const toggle = (path) => setSel(s => { const n = new Set(s); n.has(path) ? n.delete(path) : n.add(path); return n; });
  const toggleGroup = (g) => setSel(s => {
    const n = new Set(s); const all = g.photos.every(p => n.has(p.path));
    g.photos.forEach(p => all ? n.delete(p.path) : n.add(p.path)); return n;
  });
  const fmt = (ms) => ms ? new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";

  const assign = async () => {
    if (target == null || sel.size === 0) return;
    await onAssign([...sel], target);
    setSel(new Set());
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0b1c2d] text-white flex flex-col" data-testid="sort-board">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
        <Stack weight="fill" size={26} className="text-teal-400"/>
        <div className="flex-1">
          <div className="font-black text-lg leading-tight">Sort photos into rooms</div>
          <div className="text-xs text-slate-400"><span data-testid="sort-count">{photos.length}</span> unsorted · tap photos or a whole group, then pick a room</div>
        </div>
        <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-testid="sort-close"><X size={20}/></button>
      </div>

      {photos.length > 0 && (
        <div className="px-3 pt-3 flex gap-2">
          <button onClick={runAi} disabled={aiBusy} data-testid="sort-ai"
            className="flex-1 h-12 rounded-xl bg-[#13263a] border border-teal-400/40 font-black text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            <Sparkle weight="fill" size={18} className="text-teal-300"/> {aiBusy ? "AI sorting…" : suggested.length ? "Re-run AI sort" : "AI sort into rooms"}
          </button>
          {suggested.length > 0 && (
            <button onClick={applyAll} data-testid="sort-apply-ai" className="flex-1 h-12 rounded-xl bg-teal-500 text-slate-900 font-black text-sm">
              Move {suggested.length} as suggested
            </button>
          )}
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-3 pb-44 space-y-4">
        {photos.length === 0 && (
          <div className="text-center text-slate-400 py-16">
            Tray is empty.
            <button onClick={onImportMore} className="block mx-auto mt-4 h-12 px-5 rounded-2xl bg-teal-500 text-slate-900 font-black" data-testid="sort-import-more">Import photos</button>
          </div>
        )}
        {groups.map((g, gi) => {
          const allSel = g.photos.every(p => sel.has(p.path));
          return (
            <div key={gi} data-testid={`sort-group-${gi}`}>
              <button onClick={() => toggleGroup(g)} className="w-full flex items-center gap-2 mb-1.5 text-left" data-testid={`sort-group-toggle-${gi}`}>
                <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${allSel ? "bg-teal-500 border-teal-500 text-slate-900" : "border-white/30"}`}>{allSel && <Check weight="bold" size={14}/>}</span>
                {g.roomIdx != null ? (
                  <span className="font-bold text-sm flex items-center gap-1.5"><Sparkle weight="fill" size={14} className="text-teal-300"/> {roomLabel(rooms[g.roomIdx])} · {g.photos.length}</span>
                ) : g.roomIdx === null ? (
                  <span className="font-bold text-sm text-slate-400">No suggestion · {g.photos.length}</span>
                ) : (
                  <><span className="font-bold text-sm">Group {gi + 1} · {g.photos.length} photo{g.photos.length === 1 ? "" : "s"}</span>
                  <span className="text-xs text-slate-400">{fmt(g.start)}{g.end !== g.start ? ` – ${fmt(g.end)}` : ""}</span></>
                )}
              </button>
              <div className="grid grid-cols-4 gap-1.5">
                {g.photos.map(p => {
                  const on = sel.has(p.path);
                  return (
                    <button key={p.path} onClick={() => toggle(p.path)} data-testid={`sort-photo-${p.path.split("/").pop()}`}
                      className={`relative aspect-square rounded-lg overflow-hidden ${on ? "ring-4 ring-teal-400" : ""}`}>
                      <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" loading="lazy"/>
                      {p.suggested_item && <span className="absolute bottom-0 inset-x-0 bg-black/60 text-[9px] px-1 truncate">{p.suggested_item}</span>}
                      {on && <span className="absolute top-1 right-1 w-6 h-6 rounded-full bg-teal-500 text-slate-900 flex items-center justify-center"><Check weight="bold" size={14}/></span>}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {photos.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-[#0e2236] border-t border-white/10 p-3 space-y-2" data-testid="sort-bar">
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {rooms.map((r, i) => (
              <button key={i} onClick={() => setTarget(i)} data-testid={`sort-room-${i}`}
                className={`shrink-0 h-11 px-3 rounded-xl flex items-center gap-1.5 text-sm font-bold ${target === i ? "bg-teal-500 text-slate-900" : "bg-white/10 text-slate-200"}`}>
                <RoomIcon icon={r.icon} size={16}/> {roomLabel(r)}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => { if (sel.size && window.confirm(`Delete ${sel.size} photo${sel.size === 1 ? "" : "s"}?`)) { onDelete([...sel]); setSel(new Set()); } }} disabled={!sel.size}
              className="w-14 h-14 rounded-2xl bg-red-600/80 disabled:opacity-30 flex items-center justify-center" data-testid="sort-delete"><Trash weight="fill" size={22}/></button>
            <button onClick={assign} disabled={!sel.size || target == null} data-testid="sort-assign"
              className="flex-1 h-14 rounded-2xl bg-teal-500 disabled:bg-white/10 disabled:text-slate-400 text-slate-900 font-black text-lg flex items-center justify-center gap-2">
              {sel.size ? `Move ${sel.size}` : "Select photos"} {target != null && sel.size ? <>→ {roomLabel(rooms[target])}</> : null} <ArrowRight weight="bold" size={20}/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
